
"use client"

import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { Check, ChevronDown, ChevronUp } from "lucide-react"

import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/use-mobile"
import { SelectionSheet } from "@/components/ui/selection-sheet"

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Itera children do <Select> e extrai map { value: label } dos <SelectItem>.
 * Fallback recursivo pra wrappers (SelectGroup); warning em dev se value não é string.
 */
function extractOptionsFromChildren(children: React.ReactNode): Record<string, React.ReactNode> {
  const map: Record<string, React.ReactNode> = {}

  function visit(node: React.ReactNode) {
    React.Children.forEach(node, (child) => {
      if (!React.isValidElement(child)) return
      const props = child.props as { value?: unknown; children?: React.ReactNode }
      const displayName = (child.type as { displayName?: string })?.displayName
      if (displayName === SelectPrimitive.Item.displayName) {
        if (typeof props.value === 'string') {
          map[props.value] = props.children
        } else if (import.meta.env.DEV) {
          console.warn('[Select] SelectItem.value não é string:', props.value)
        }
      } else if (props.children) {
        visit(props.children)
      }
    })
  }

  visit(children)
  return map
}

// ---------------------------------------------------------------------------
// Mobile context
// ---------------------------------------------------------------------------

interface SelectMobileContextValue {
  isMobile: boolean
  open: boolean
  setOpen: (open: boolean) => void
  value: string | undefined
  onValueChange: (value: string) => void
  optionsMap: Record<string, React.ReactNode>
}

const SelectMobileContext = React.createContext<SelectMobileContextValue | null>(null)

// ---------------------------------------------------------------------------
// Select (root)
// ---------------------------------------------------------------------------

function Select({
  children,
  value,
  onValueChange,
  defaultValue,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  const isMobile = useIsMobile()
  const [internalOpen, setInternalOpen] = React.useState(false)
  const [internalValue, setInternalValue] = React.useState<string | undefined>(defaultValue)

  const effectiveValue = value ?? internalValue

  const optionsMap = React.useMemo(() => extractOptionsFromChildren(children), [children])

  const handleValueChange = React.useCallback(
    (v: string) => {
      setInternalValue(v)
      onValueChange?.(v)
    },
    [onValueChange]
  )

  if (isMobile) {
    return (
      <SelectMobileContext.Provider
        value={{
          isMobile: true,
          open: internalOpen,
          setOpen: setInternalOpen,
          value: effectiveValue,
          onValueChange: handleValueChange,
          optionsMap,
        }}
      >
        {children}
      </SelectMobileContext.Provider>
    )
  }

  return (
    <SelectPrimitive.Root
      value={value}
      onValueChange={onValueChange}
      defaultValue={defaultValue}
      {...props}
    >
      {children}
    </SelectPrimitive.Root>
  )
}

// ---------------------------------------------------------------------------
// SelectGroup / SelectSeparator — unchanged (mobile doesn't touch these)
// ---------------------------------------------------------------------------

const SelectGroup = SelectPrimitive.Group

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

// ---------------------------------------------------------------------------
// SelectScrollUpButton / SelectScrollDownButton — desktop only, unchanged
// ---------------------------------------------------------------------------

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
))
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn("flex cursor-default items-center justify-center py-1", className)}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
))
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName

// ---------------------------------------------------------------------------
// SelectValue
// ---------------------------------------------------------------------------

const SelectValue = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Value>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Value>
>(({ placeholder, ...props }, ref) => {
  const ctx = React.useContext(SelectMobileContext)

  if (ctx?.isMobile) {
    const label = ctx.value ? ctx.optionsMap[ctx.value] : undefined
    return (
      <span className={cn("flex-1 min-w-0 truncate text-left", !ctx.value && "text-muted-foreground")}>
        {label !== undefined ? label : (ctx.value ?? placeholder)}
      </span>
    )
  }

  return <SelectPrimitive.Value ref={ref} placeholder={placeholder} {...props} />
})
SelectValue.displayName = SelectPrimitive.Value.displayName

// ---------------------------------------------------------------------------
// SelectTrigger
// ---------------------------------------------------------------------------

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const ctx = React.useContext(SelectMobileContext)

  if (ctx?.isMobile) {
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type="button"
        role="combobox"
        aria-expanded={ctx.open}
        onClick={() => ctx.setOpen(true)}
        className={cn(
          "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...(props as React.HTMLAttributes<HTMLButtonElement>)}
      >
        {children}
        <ChevronDown className="h-4 w-4 opacity-50" />
      </button>
    )
  }

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="h-4 w-4 opacity-50" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  )
})
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

// ---------------------------------------------------------------------------
// SelectContent
// ---------------------------------------------------------------------------

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => {
  const ctx = React.useContext(SelectMobileContext)

  if (ctx?.isMobile) {
    return (
      <SelectionSheet
        open={ctx.open}
        onOpenChange={ctx.setOpen}
        title="Selecione"
      >
        <div className="divide-y">{children}</div>
      </SelectionSheet>
    )
  }

  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        className={cn(
          "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          position === "popper" &&
            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
          className
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          className={cn(
            "p-1",
            position === "popper" &&
              "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  )
})
SelectContent.displayName = SelectPrimitive.Content.displayName

// ---------------------------------------------------------------------------
// SelectLabel
// ---------------------------------------------------------------------------

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("py-1.5 pl-8 pr-2 text-sm font-semibold", className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

// ---------------------------------------------------------------------------
// SelectItem
// ---------------------------------------------------------------------------

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, value, ...props }, ref) => {
  const ctx = React.useContext(SelectMobileContext)
  const valueStr = String(value)

  if (ctx?.isMobile) {
    const selected = ctx.value === valueStr
    return (
      <button
        type="button"
        className={cn(
          "flex w-full items-center gap-2 px-4 py-3 text-left text-sm hover:bg-muted",
          selected && "bg-muted",
          className
        )}
        onClick={() => {
          ctx.onValueChange(valueStr)
          ctx.setOpen(false)
        }}
      >
        <Check className={cn("h-4 w-4 shrink-0", selected ? "opacity-100" : "opacity-0")} />
        <span className="flex-1 truncate">{children}</span>
      </button>
    )
  }

  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        className
      )}
      value={value}
      {...props}
    >
      <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <Check className="h-4 w-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  )
})
SelectItem.displayName = SelectPrimitive.Item.displayName

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
}
